import { useState, useMemo, useRef } from 'react';
import { Plus, Boxes, History } from 'lucide-react';
import { useFormikContext } from 'formik';

import { useUsers } from '@/portainer/users/queries/useUsers';
import { useTeams } from '@/react/portainer/users/teams/queries';
import { useRbacRoles } from '@/react/portainer/users/RolesView/useRbacRoles';
import { pluralize } from '@/react/common/string-utils';

import { FormControl } from '@@/form-components/FormControl';
import { PortainerSelect } from '@@/form-components/PortainerSelect';
import { TextTip } from '@@/Tip/TextTip';
import { Button } from '@@/buttons';
import { FormSection } from '@@/form-components/FormSection';
import { BoxSelector, BoxSelectorOption } from '@@/BoxSelector';

import { MultiUserTeamSelector } from '../../MultiUserTeamSelector';

import { AccessListDatatable } from './AccessListDatatable';
import { NamespaceSelect } from './NamespaceSelect';
import {
  KubernetesRbacFormValues,
  KubernetesRbacMode,
  KubernetesUnifiedAccess,
} from './types';

// Namespaced roles that require namespace specification
const NAMESPACE_ROLE_IDS = [3, 4, 6]; // Standard, Read-only, Namespace Operator

const RBAC_MODE_OPTIONS: ReadonlyArray<BoxSelectorOption<KubernetesRbacMode>> =
  [
    {
      id: 'rbac-mode-additive',
      value: 'additive',
      icon: Boxes,
      iconType: 'badge',
      label: 'Native Kubernetes RBAC',
      description:
        'Recommended. Permissions accumulate across cluster, team and per-namespace roles, exactly as native Kubernetes RBAC behaves.',
    },
    {
      id: 'rbac-mode-least-privilege',
      value: 'least-privilege',
      icon: History,
      iconType: 'badge',
      label: 'Legacy Portainer privileges',
      description:
        'Where roles overlap, the most restrictive role takes precedence (least privilege), matching legacy Portainer behavior.',
    },
  ];

type UserTeamValue = {
  Type: 'user' | 'team';
  Id: number;
  Name: string;
};

export function KubernetesRBACFormFields() {
  const {
    values: { accessList = [], mode = 'least-privilege' },
    setFieldValue,
  } = useFormikContext<KubernetesRbacFormValues>();
  const [tempSelectedRole, setTempSelectedRole] = useState<number>(0);
  const [tempSelectedUsersAndTeams, setTempSelectedUsersAndTeams] = useState<
    UserTeamValue[]
  >([]);
  const [tempSelectedNamespaces, setTempSelectedNamespaces] = useState<
    string[]
  >([]);
  const newIdCounter = useRef(accessList.length || 1);

  const userOptionsQuery = useUsers(false, undefined, true, (users) =>
    users.map((user) => ({ value: user.Id, label: user.Username }))
  );
  const teamOptionsQuery = useTeams(false, undefined, {
    select: (teams) => teams.map((t) => ({ value: t.Id, label: t.Name })),
  });
  const roleOptionsQuery = useRbacRoles({
    select: (roles) =>
      roles
        .filter((role) => role.Scope && role.Scope.kubernetes)
        .map((role) => ({ value: role.Id, label: role.Name })),
  });

  const userOptions = userOptionsQuery.data || [];
  const teamOptions = teamOptionsQuery.data || [];
  const roleOptions = roleOptionsQuery.data || [];
  const isUserOptionsLoading = userOptionsQuery.isLoading;
  const isTeamOptionsLoading = teamOptionsQuery.isLoading;

  // Combine users and teams into one list with proper structure
  const usersAndTeamsOptions = [
    ...userOptions.map((u) => ({
      label: u.label,
      value: { Type: 'user' as const, Id: u.value, Name: u.label },
    })),
    ...teamOptions.map((t) => ({
      label: t.label,
      value: { Type: 'team' as const, Id: t.value, Name: t.label },
    })),
  ];

  // Get the selected role name
  const selectedRoleName = useMemo(() => {
    const role = roleOptionsQuery.data?.find(
      (r) => r.value === tempSelectedRole
    );
    return role?.label || '';
  }, [tempSelectedRole, roleOptionsQuery.data]);

  // Check if selected role requires namespace specification
  const isNamespaceRole = NAMESPACE_ROLE_IDS.includes(tempSelectedRole);

  // Check if the Add button should be enabled
  const canAddAccess =
    tempSelectedUsersAndTeams.length > 0 &&
    tempSelectedRole > 0 &&
    (!isNamespaceRole || tempSelectedNamespaces.length > 0);

  return (
    <>
      <FormSection title="Permission model" className="mb-8">
        <BoxSelector
          radioName="rbac-mode"
          options={RBAC_MODE_OPTIONS}
          value={mode}
          onChange={(value) => setFieldValue('mode', value)}
          slim={true}
        />
      </FormSection>

      <FormSection title="Add access" className="mb-8">
        <FormControl label="Users/teams" required>
          <MultiUserTeamSelector
            options={usersAndTeamsOptions}
            value={tempSelectedUsersAndTeams}
            onChange={setTempSelectedUsersAndTeams}
            isLoading={isUserOptionsLoading || isTeamOptionsLoading}
          />
        </FormControl>

        <FormControl label="Role" required>
          <PortainerSelect<number>
            value={tempSelectedRole}
            onChange={(value) => setTempSelectedRole(value || 0)}
            options={roleOptions}
            placeholder="Select a role"
            bindToBody
            data-cy="rbac-role-select"
          />
        </FormControl>

        {/* Show namespace input for namespace-scoped roles */}
        {isNamespaceRole && (
          <FormControl label="Namespaces" required>
            <p className="text-muted mb-2 text-xs">
              The <strong>{selectedRoleName}</strong> role is namespace-scoped.
              Select the namespaces that the user(s) / team(s) should have
              access to:
            </p>
            <NamespaceSelect
              selectedNamespaces={tempSelectedNamespaces}
              onChange={setTempSelectedNamespaces}
              data-cy="rbac-namespaces-input"
              namespaces={getNamespacesFromAccessList()}
            />
          </FormControl>
        )}

        <div className="form-group">
          <div className="col-sm-12 flex gap-2">
            <Button
              icon={Plus}
              color="primary"
              size="small"
              disabled={!canAddAccess}
              onClick={handleAddAccess}
              data-cy="add-access-button"
              className="!ml-0"
            >
              Add Access
            </Button>
          </div>
        </div>
      </FormSection>

      <div className="-mx-4">
        <AccessListDatatable
          userOptions={userOptions}
          teamOptions={teamOptions}
          roleOptions={roleOptions}
          newIdCounter={newIdCounter.current}
          setNewIdCounter={(val: number) => {
            newIdCounter.current = val;
          }}
        />
      </div>

      {/* Access Info message */}
      <div className="form-group">
        <div className="col-sm-12 mt-2">
          {accessList.length > 0 && (
            <TextTip color="blue">
              {(() => {
                const userCount = accessList.filter(
                  (a) => a.Type === 'user'
                ).length;
                const teamCount = accessList.filter(
                  (a) => a.Type === 'team'
                ).length;
                const envAccessCount = accessList.filter(
                  (a) => a.Role.Id && !NAMESPACE_ROLE_IDS.includes(a.Role.Id)
                ).length;
                const namespaceAccessCount = accessList.filter(
                  (a) => a.Role.Id && NAMESPACE_ROLE_IDS.includes(a.Role.Id)
                ).length;
                const userString = pluralize(userCount, 'user');
                const teamString = pluralize(teamCount, 'team');

                return `Access configured for ${userCount} ${userString} and ${teamCount} ${teamString} (${envAccessCount} environment access, ${namespaceAccessCount} namespace access).`;
              })()}
            </TextTip>
          )}
        </div>
      </div>
    </>
  );

  function handleAddAccess() {
    if (!canAddAccess) return;

    const namespaces = cleanNamespaces(tempSelectedNamespaces);
    let newAccessList = [...accessList];

    newAccessList = tempSelectedUsersAndTeams.reduce((result, selection) => {
      if (selection.Type === 'user') {
        return addOrUpdateUser(result, selection.Id, namespaces);
      }
      if (selection.Type === 'team') {
        return addOrUpdateTeam(result, selection.Id, namespaces);
      }

      return result;
    }, newAccessList);

    setFieldValue('accessList', newAccessList);
    resetSelections();
  }

  function addOrUpdateUser(
    accessList: KubernetesUnifiedAccess[],
    userId: number,
    namespaces: string[]
  ): KubernetesUnifiedAccess[] {
    const userEntry = createUserAccess(userId, namespaces);
    if (!userEntry) return accessList;

    const existingIndex = accessList.findIndex(
      (a) => a.Type === 'user' && a.PartyId === userId
    );

    if (existingIndex === -1) {
      return [...accessList, userEntry];
    }

    const updatedEntry = { ...accessList[existingIndex], ...userEntry };
    const beforeEntry = accessList.slice(0, existingIndex);
    const afterEntry = accessList.slice(existingIndex + 1);
    return [...beforeEntry, updatedEntry, ...afterEntry];
  }

  function addOrUpdateTeam(
    accessList: KubernetesUnifiedAccess[],
    teamId: number,
    namespaces: string[]
  ): KubernetesUnifiedAccess[] {
    const teamEntry = createTeamAccess(teamId, namespaces);
    if (!teamEntry) return accessList;

    const existingIndex = accessList.findIndex(
      (a) => a.Type === 'team' && a.PartyId === teamId
    );

    if (existingIndex === -1) {
      return [...accessList, teamEntry];
    }

    const updatedEntry = { ...accessList[existingIndex], ...teamEntry };
    const beforeEntry = accessList.slice(0, existingIndex);
    const afterEntry = accessList.slice(existingIndex + 1);
    return [...beforeEntry, updatedEntry, ...afterEntry];
  }

  function createUserAccess(
    userId: number,
    namespaces: string[]
  ): KubernetesUnifiedAccess | null {
    const user = userOptions.find((u) => u.value === userId);
    if (!user) return null;

    const access: KubernetesUnifiedAccess = {
      Id: newIdCounter.current,
      PartyId: userId,
      Type: 'user',
      Name: user.label,
      Role: { Id: tempSelectedRole, Name: selectedRoleName },
      namespaces: isNamespaceRole ? namespaces : undefined,
    };
    newIdCounter.current++;
    return access;
  }

  function createTeamAccess(
    teamId: number,
    namespaces: string[]
  ): KubernetesUnifiedAccess | null {
    const team = teamOptions.find((t) => t.value === teamId);
    if (!team) return null;

    const access: KubernetesUnifiedAccess = {
      Id: newIdCounter.current,
      PartyId: teamId,
      Type: 'team',
      Name: team.label,
      Role: { Id: tempSelectedRole, Name: selectedRoleName },
      namespaces: isNamespaceRole ? namespaces : undefined,
    };
    newIdCounter.current++;
    return access;
  }

  function cleanNamespaces(input: string[]): string[] {
    return input.map((ns) => ns.trim()).filter((ns) => ns !== '');
  }

  function resetSelections() {
    setTempSelectedUsersAndTeams([]);
    setTempSelectedNamespaces([]);
  }

  function getNamespacesFromAccessList(): string[] {
    return Array.from(new Set(accessList.flatMap((a) => a.namespaces || [])));
  }
}
